import { type FC, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

interface CodeRendererProps {
  content: string;
  language: string | null;
}

export const CodeRenderer: FC<CodeRendererProps> = ({ content, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  };

  if (!content) {
    return (
      <div className="cuadra-p-4 cuadra-text-sm cuadra-text-muted-foreground">
        No content
      </div>
    );
  }

  return (
    <div className="cuadra-flex cuadra-flex-col cuadra-h-full">
      <div className="cuadra-flex cuadra-items-center cuadra-justify-between cuadra-px-4 cuadra-py-2 cuadra-border-b cuadra-border-border cuadra-bg-muted">
        <span className="cuadra-text-xs cuadra-font-mono cuadra-text-muted-foreground">
          {language ?? "plaintext"}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="cuadra-flex cuadra-items-center cuadra-gap-1 cuadra-text-xs cuadra-text-muted-foreground hover:cuadra-text-foreground cuadra-transition-colors"
          aria-label={copied ? "Copied" : "Copy code"}
        >
          {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {/* Main file container: white (light) / slight off-white (dark), black text */}
      <pre className="cuadra-flex-1 cuadra-overflow-auto cuadra-p-4 cuadra-m-0 cuadra-text-sm cuadra-font-mono cuadra-leading-relaxed cuadra-bg-white dark:cuadra-bg-[#f5f5f4] cuadra-text-black">
        <code>{content}</code>
      </pre>
    </div>
  );
};
